import { format } from "date-fns";
import type { ReactNode } from "react";

import { Button, LinkButton } from "@/components/ui/button";
import { TemplateThumbnailCard } from "@/components/ui/report/TemplateThumbnailCard/TemplateThumbnailCard";
import useFeature from "@/lib/feature-management/useFeature";
import { useTemplateLayoutList } from "@/lib/template-studio/repository/useTemplateLayoutList";
import { templateMatchesSearch } from "@/lib/template-studio/templateLayoutSearch";
import { TEMPLATE_CREATE_PATH } from "@/lib/template-studio/templateStudioEntry";
import { cn } from "@/lib/utils";

const TEMPLATE_LOADING_SKELETON_VISIBLE_ON_SMALL = 2;
const TEMPLATE_LOADING_SKELETON_KEYS = [
  "choose-template-loading-skeleton-1",
  "choose-template-loading-skeleton-2",
  "choose-template-loading-skeleton-3",
  "choose-template-loading-skeleton-4",
] as const;

const templateUpdatedLabel = (updatedAt: string) =>
  `Updated ${format(new Date(updatedAt), "MMM d yyyy")}`;

const templateCountLabel = (count: number) =>
  `${count} ${count === 1 ? "template" : "templates"}`;

const CHOOSE_TEMPLATE_CONTENT_CLASS = "pl-[76px] pr-16";

interface ChooseTemplateListChromeProps {
  templateCount: number;
  children: ReactNode;
}

const ChooseTemplateListChrome = ({
  templateCount,
  children,
}: ChooseTemplateListChromeProps) => (
  <section className="flex min-h-0 flex-1 flex-col">
    <div
      className={cn(
        "min-h-12 border-b border-gray-200 py-4",
        CHOOSE_TEMPLATE_CONTENT_CLASS,
      )}
    >
      <p className="text-xs font-medium tracking-[0.12px] text-gray-600">
        {templateCountLabel(templateCount)}
      </p>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
  </section>
);

export interface ChooseTemplateViewProps {
  searchTerm: string;
  onSelectTemplate: (templateId: string) => void;
}

export const ChooseTemplateView = ({
  searchTerm,
  onSelectTemplate,
}: ChooseTemplateViewProps) => {
  const canCreateTemplate = useFeature("can-create-templates", false);
  const { items, isLoading, error, isRetryable, retry } =
    useTemplateLayoutList();

  const templates = items.filter((template) => template.status !== "archived");
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredTemplates = templates.filter((template) =>
    templateMatchesSearch(template, normalizedSearchTerm),
  );
  const hasTemplates = templates.length > 0;
  const hasVisibleTemplates = filteredTemplates.length > 0;

  if (isLoading) {
    return (
      <section className="flex min-h-0 flex-1 flex-col">
        <div
          className={cn(
            "min-h-12 border-b border-gray-200 py-4",
            CHOOSE_TEMPLATE_CONTENT_CLASS,
          )}
          aria-hidden
        />
        <ul
          className={cn(
            "grid grid-cols-1 gap-8 py-8 sm:grid-cols-[repeat(auto-fill,350px)]",
            CHOOSE_TEMPLATE_CONTENT_CLASS,
          )}
          aria-live="polite"
          aria-busy="true"
        >
          {TEMPLATE_LOADING_SKELETON_KEYS.map((key, index) => (
            <li
              key={key}
              className={cn(
                "min-w-0",
                index >= TEMPLATE_LOADING_SKELETON_VISIBLE_ON_SMALL &&
                  "hidden sm:block",
              )}
            >
              <TemplateThumbnailCard isLoading />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (error && !hasTemplates) {
    return (
      <div
        className={cn(
          "flex flex-col items-center gap-3 py-16 text-center",
          CHOOSE_TEMPLATE_CONTENT_CLASS,
        )}
        role="alert"
        aria-live="polite"
      >
        <p className="max-w-md text-sm text-gray-700">{error.message}</p>
        {isRetryable ? (
          <Button variant="outline" size="sm" onPress={retry}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  if (!hasTemplates) {
    return (
      <section className={cn("pb-8", CHOOSE_TEMPLATE_CONTENT_CLASS)}>
        <div className="flex items-center gap-4 pt-6 pb-2">
          <p className="text-xs font-medium tracking-[0.12px] text-gray-600">
            Active (0)
          </p>
          <div aria-hidden className="h-px flex-1 bg-gray-200" />
        </div>
        <div className="flex flex-col items-center gap-2 pt-[86px] text-center">
          <h2 className="text-base font-medium text-gray-900">
            {canCreateTemplate
              ? "No templates yet, create one to get started"
              : "No templates yet"}
          </h2>
          <p className="max-w-md text-sm text-gray-700">
            {canCreateTemplate
              ? "Templates will appear here once they've been created."
              : "Templates will appear here once they've been created by admin."}
          </p>
          {canCreateTemplate ? (
            <LinkButton to={TEMPLATE_CREATE_PATH} size="sm" className="mt-3">
              Create a template
            </LinkButton>
          ) : null}
        </div>
      </section>
    );
  }

  if (!hasVisibleTemplates) {
    return (
      <ChooseTemplateListChrome templateCount={0}>
        <p
          className={cn(
            "pt-4 pb-8 text-sm text-gray-700",
            CHOOSE_TEMPLATE_CONTENT_CLASS,
          )}
        >
          No templates match your search. Try a different name or description.
        </p>
      </ChooseTemplateListChrome>
    );
  }

  return (
    <ChooseTemplateListChrome templateCount={filteredTemplates.length}>
      {error ? (
        <div className={cn("mt-4", CHOOSE_TEMPLATE_CONTENT_CLASS)}>
          <div
            className="flex items-center justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="alert"
          >
            <p>{error.message} Showing the last loaded templates.</p>
            {isRetryable ? (
              <Button variant="outline" size="sm" onPress={retry}>
                Try again
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ul
        className={cn(
          "grid grid-cols-1 items-stretch gap-8 py-8 sm:grid-cols-[repeat(auto-fill,350px)]",
          CHOOSE_TEMPLATE_CONTENT_CLASS,
        )}
      >
        {filteredTemplates.map((template) => (
          <li key={template.id} className="flex min-w-0">
            <button
              type="button"
              onClick={() => onSelectTemplate(template.id)}
              className="flex h-full w-full min-w-0 cursor-pointer rounded-xl text-left outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-inset"
            >
              <TemplateThumbnailCard
                className="h-full"
                isInteractive
                name={template.name}
                description={template.description}
                updatedLabel={templateUpdatedLabel(template.updatedAt)}
                slotCount={template.slotCount}
                briefingCount={template.briefingCount}
              />
            </button>
          </li>
        ))}
      </ul>
    </ChooseTemplateListChrome>
  );
};
